<!-- 
  Shared component: used by both microscope (Vue 3) and periscope (Vue 2.7).
  Uses dual bindings (:value/@input + :model-value/@update:modelValue) for compatibility.
-->
<template>
  <div ref="containerRef" class="conversations">
    <div class="grid-layout">
      <div v-if="isCompact" class="compact-sidebar">
        <AppSelect
          :value="props.controller.selectedConversationId.value"
          :model-value="props.controller.selectedConversationId.value"
          :options="conversationSelectOptions"
          @input="handleSelectConversation($event)"
          @update:modelValue="handleSelectConversation($event)"
        />
        <div class="compact-sidebar-actions">
          <AppButton
            icon
            :loading="isRefreshingConversations"
            aria-label="Uppdatera"
            title="Uppdatera"
            @click="handleRefreshConversations"
          >
            <SvgIcon :path="mdiRefresh" />
          </AppButton>
          <AppButton icon aria-label="Ny konversation" title="Ny konversation" @click="handleCreateNewConversation">
            <SvgIcon :path="mdiPlus" />
          </AppButton>
        </div>
      </div>

      <div v-else class="sidebar">
        <div class="sidebar-header">
          <span class="sub-header">Meddelanden</span>
          <div class="sidebar-header-actions">
            <AppButton
              icon
              :loading="isRefreshingConversations"
              aria-label="Uppdatera"
              title="Uppdatera"
              @click="handleRefreshConversations"
            >
              <SvgIcon :path="mdiRefresh" />
            </AppButton>
            <AppButton icon aria-label="Ny konversation" title="Ny konversation" @click="handleCreateNewConversation">
              <SvgIcon :path="mdiPlus" />
            </AppButton>
          </div>
        </div>

        <div class="sidebar-content">
          <ConversationList
            :conversations="props.controller.conversationsList.value"
            :active-conversation-id="props.controller.selectedConversationId.value"
            @select="handleSelectConversation($event)"
          />
        </div>
      </div>

      <div v-if="creatingNewConversation" class="message-container">
        <span class="sub-header">Skapa ny konversation</span>
        <div>
          <AppSelect
            :value="selectedConversationSubject"
            :model-value="selectedConversationSubject"
            :options="typeOptions"
            placeholder="Välj ämne"
            class="mb-4"
            @input="handleTypeChange($event)"
            @update:modelValue="handleTypeChange($event)"
          />
          <AppInput
            v-if="selectedConversationSubject === 'other'"
            v-model="customSubject"
            label="Ämne"
            placeholder="Ange ämne"
            class="mb-4"
          />
          {{ customSubject }}
          <AppButton color="green" :disabled="isCreateConversationDisabled" @click="handleCreateConversation">
            Skapa
          </AppButton>
        </div>
      </div>
      <div v-else class="message-container">
        <ConversationMessages
          :conversation="props.controller.selectedConversation.value"
          :message-items="messageItems"
          :help-text="selectedConversationHelpText"
          :empty-state-title="props.controller.conversationsList.value.length ? undefined : 'Inga meddelanden ännu'"
          :empty-state-body="
            props.controller.conversationsList.value.length ? undefined : 'Klicka på + för att skapa en konversation.'
          "
          @submit-message="handleSubmitMessage($event)"
          @delete="promptDeleteMessage($event)"
          @mark-read="toggleReadState($event)"
          @toggle-status="toggleConversationStatus"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { mdiPlus, mdiRefresh } from '@mdi/js'
import {
  CONVERSATION_DISPLAY_LABELS_BY_TYPE,
  CONVERSATION_STATIC_SUBJECTS_BY_TYPE,
  type ConversationType,
  type ConversationMessage,
  type StaticSubjectConversationType,
} from '@staff/shared'

import SvgIcon from '../SvgIcon/SvgIcon.vue'
import AppButton from '../AppButton/AppButton.vue'
import AppInput from '../AppInput/AppInput.vue'
import AppSelect from '../AppSelect/AppSelect.vue'
import ConversationDeleteMessageConfirmation from './ConversationDeleteMessageConfirmation.vue'
import ConversationList from './ConversationList.vue'
import ConversationMessages from './ConversationMessages.vue'

import { useModal } from '../../composables/modal'
import { useOrganisation } from '../../composables/useOrganisation'
import type { ConversationsController, CreateConversationInput } from '../../composables/conversations'
import type { SubmitMessagePayload } from './ConversationMessages.vue'
import { describeConversationError } from './conversationErrorMessage'

const COMPACT_BREAKPOINT = 660
const POLL_INTERVAL_MS = 15000

type MessageListItem = {
  message: ConversationMessage
  alignment: 'sent' | 'received'
  replyToMessage?: ConversationMessage | null
  canReply: boolean
  canEdit: boolean
  canDelete: boolean
  canMarkRead: boolean
}

const props = defineProps<{
  controller: ConversationsController
  currentOrganisationName?: string
  applicationConversationTitle?: string
  helpTextByType?: Partial<Record<ConversationType, string>>
}>()
const { openModal, closeModal } = useModal()
const { getLastOrganisation } = useOrganisation()

const containerRef = ref<HTMLElement | null>(null)
const isCompact = ref(false)
const creatingNewConversation = ref(false)
const pendingDeleteMessage = ref<ConversationMessage | null>(null)
const selectedConversationSubject = ref<ConversationType | ''>('')
const customSubject = ref('')
let resizeObserver: ResizeObserver | null = null
let pollIntervalId: number | null = null

// TODO: Replace alert() with a toast/snackbar notification
const notifyError = (context: string, err: unknown) => {
  console.error(context, err)
  alert(`${context}: ${describeConversationError(err)}`)
}

const isRefreshingConversations = computed(
  () => props.controller.isLoadingConversations.value || props.controller.isLoadingConversation.value,
)

// --- Resize observation ---

onMounted(() => {
  if (!containerRef.value) return

  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      isCompact.value = entry.contentRect.width <= COMPACT_BREAKPOINT
    }
  })

  resizeObserver.observe(containerRef.value)

  void refresh(false)
  startPolling()
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  stopPolling()
})

// --- Polling ---

const stopPolling = () => {
  if (pollIntervalId !== null) {
    window.clearInterval(pollIntervalId)
    pollIntervalId = null
  }
}

const refresh = async (notifyOnError = false) => {
  await props.controller.refresh()

  if (notifyOnError && props.controller.conversationError.value) {
    notifyError('Ett fel uppstod när konversationerna skulle uppdateras', props.controller.conversationError.value)
  }
}

const startPolling = () => {
  stopPolling()

  pollIntervalId = window.setInterval(() => {
    if (document.hidden) return
    void refresh(false)
  }, POLL_INTERVAL_MS)
}

// --- Computed UI state ---

const conversationSelectOptions = computed(() =>
  props.controller.conversationsList.value.map((conversation) => ({
    text: conversation.subject,
    value: conversation.id,
  })),
)

const selectedConversationHelpText = computed(() => {
  const type = props.controller.selectedConversation.value?.type
  return type ? props.helpTextByType?.[type] : undefined
})

const typeOptions = computed(() => {
  switch (props.controller.target.value.kind) {
    case 'application':
      return [
        {
          text: props.applicationConversationTitle ?? CONVERSATION_DISPLAY_LABELS_BY_TYPE.application,
          value: 'application',
        },
        {
          text: CONVERSATION_DISPLAY_LABELS_BY_TYPE.preAccessControl,
          value: 'preAccessControl',
        },
        {
          text: CONVERSATION_DISPLAY_LABELS_BY_TYPE.other,
          value: 'other',
        },
      ]

    case 'debt':
      return [
        ...Object.entries(CONVERSATION_STATIC_SUBJECTS_BY_TYPE)
          .filter(([type]) => type !== 'preAccessControl')
          .map(([type, text]) => ({
            text,
            value: type as StaticSubjectConversationType,
          })),
        {
          text: CONVERSATION_DISPLAY_LABELS_BY_TYPE.other,
          value: 'other',
        },
      ]
  }
  return []
})

const isCreateConversationDisabled = computed(() => {
  if (!selectedConversationSubject.value) return true
  if (selectedConversationSubject.value !== 'other') return false
  return customSubject.value.trim().length === 0
})

const pendingDeletePreview = computed(() => {
  if (!pendingDeleteMessage.value) return ''
  if (pendingDeleteMessage.value.deletedAt) return 'Det här meddelandet har redan tagits bort.'

  const preview = pendingDeleteMessage.value.message.trim()
  if (!preview) return 'Meddelandet saknar innehåll.'

  return preview.length > 120 ? `${preview.slice(0, 120)}…` : preview
})

const messageItems = computed<MessageListItem[]>(() => {
  const conversation = props.controller.selectedConversation.value
  if (!conversation) return []

  const currentOrganisationName = props.currentOrganisationName ?? getLastOrganisation()

  return conversation.messages.map((message) => {
    const isOwnMessage = message.author.organisationName === currentOrganisationName
    const replyToMessage = message.inReplyToMessageId
      ? (conversation.messages.find((candidate) => candidate.id === message.inReplyToMessageId) ?? null)
      : null

    return {
      message,
      alignment: isOwnMessage ? 'sent' : 'received',
      replyToMessage,
      canReply: !message.deletedAt && conversation.status === 'open',
      canEdit: isOwnMessage && !message.deletedAt,
      canDelete: isOwnMessage && !message.deletedAt,
      canMarkRead: !isOwnMessage && !message.deletedAt,
    }
  })
})

// --- Handlers ---

const handleCreateNewConversation = () => {
  creatingNewConversation.value = true
  selectedConversationSubject.value = ''
  customSubject.value = ''
  handleSelectConversation()
}

const handleTypeChange = (value?: string) => {
  selectedConversationSubject.value = (value ?? '') as ConversationType | ''

  if (selectedConversationSubject.value !== 'other') {
    customSubject.value = ''
  }
}

const handleSelectConversation = async (conversationId?: string) => {
  if (!conversationId) return
  creatingNewConversation.value = false
  await props.controller.selectConversation(conversationId)

  if (props.controller.conversationError.value) {
    notifyError('Ett fel uppstod när konversationen skulle hämtas', props.controller.conversationError.value)
  }
}

const handleCreateConversation = async () => {
  if (!selectedConversationSubject.value) return

  const conversationInput: CreateConversationInput =
    selectedConversationSubject.value === 'other'
      ? {
          type: 'other',
          subject: customSubject.value.trim(),
        }
      : {
          type: selectedConversationSubject.value,
        }

  await props.controller.createConversation(conversationInput)

  if (props.controller.conversationError.value) {
    notifyError('Ett fel uppstod när konversationen skulle skapas', props.controller.conversationError.value)
    return
  }

  creatingNewConversation.value = false
  selectedConversationSubject.value = ''
  customSubject.value = ''
}

const handleRefreshConversations = async () => {
  await refresh(true)
}

const handleSubmitMessage = async (payload: SubmitMessagePayload) => {
  if (payload.editMessageId) {
    await props.controller.updateMessage(payload.editMessageId, {
      message: payload.message,
    })

    if (props.controller.conversationError.value) {
      notifyError('Ett fel uppstod när meddelandet skulle uppdateras', props.controller.conversationError.value)
    }
  } else {
    await props.controller.createMessage({
      message: payload.message,
      inReplyToMessageId: payload.inReplyToMessageId,
    })

    if (props.controller.conversationError.value) {
      notifyError('Ett fel uppstod när meddelandet skulle skickas', props.controller.conversationError.value)
    }
  }
}

const promptDeleteMessage = (message: ConversationMessage) => {
  pendingDeleteMessage.value = message
  openModal(
    'Ta bort meddelande?',
    ConversationDeleteMessageConfirmation,
    {
      preview: pendingDeletePreview.value,
      onConfirm: confirmDeleteMessage,
    },
    { onClose: closeDeleteConfirmation },
  )
}

const closeDeleteConfirmation = () => {
  pendingDeleteMessage.value = null
}

const confirmDeleteMessage = async () => {
  if (!pendingDeleteMessage.value) return

  await props.controller.deleteMessage(pendingDeleteMessage.value.id)

  if (props.controller.conversationError.value) {
    notifyError('Ett fel uppstod när meddelandet skulle tas bort', props.controller.conversationError.value)
  }

  closeModal()
}

const toggleReadState = async (message: ConversationMessage) => {
  await props.controller.updateMessage(message.id, {
    markAsRead: !message.readAt,
  })

  if (props.controller.conversationError.value) {
    notifyError('Ett fel uppstod när meddelandet skulle uppdateras', props.controller.conversationError.value)
  }
}

const toggleConversationStatus = async () => {
  await props.controller.toggleConversationStatus()

  if (props.controller.conversationError.value) {
    notifyError(
      'Ett fel uppstod när konversationens status skulle uppdateras',
      props.controller.conversationError.value,
    )
  }
}
</script>

<style scoped lang="scss">
.conversations {
  background-color: var(--color-canvas-inset);
  display: flex;
  flex-direction: column;
  container-type: inline-size;
  gap: 4px;
  height: 100%;
}

.grid-layout {
  flex: 1;
  display: grid;
  grid-template-columns: minmax(16rem, 1fr) minmax(0, 3fr);
  grid-template-rows: minmax(0, 1fr);
  min-height: 0;
  gap: 4px;
}

@container (max-width: 660px) {
  .grid-layout {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }
}

.compact-sidebar {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: var(--color-canvas-default);
  padding: 12px 16px;
}

.compact-sidebar-actions,
.sidebar-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sidebar {
  background-color: var(--color-canvas-default);
  padding: 16px;
  overflow-y: auto;
  min-height: 0;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
}

.message-container {
  min-height: 0;
  background-color: var(--color-canvas-default);
  padding: 16px;
}

.sub-header {
  color: var(--color-fg-subtle);
}
</style>
